import { useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ApiError, DocumentsService, ExcelExportsService, JobStatusResponse } from 'api';
import { useCheckReportFile } from 'services/hooks';
import { OptionalStatus } from 'services/hooks/useCheckReportFile/useCheckReportFile.props';
import { FIRM_LEVEL_FOLDERS_QUERY_KEY, FIRM_LEVEL_SUMMARY_QUERY_KEY } from './useFirmFolders.constants';


interface FirmLevelScopeParams {
  firmId?: number | null;
  // Company view: only that company's firm folders (e.g. Daily NAV / <company>).
  companyId?: number | null;
  enabled?: boolean;
}

// The poller narrows http_status to its own status-code union.
type ZipJobStatus = Omit<JobStatusResponse, 'http_status'> & OptionalStatus;

// Accepted / not-yet-visible job statuses keep the zip poll going.
const ZIP_KEEP_POLLING_STATUS_CODES = [202, 404];

const errorMessage = (error: unknown, fallback: string) =>
  (error instanceof ApiError && (error.body as { detail?: string } | undefined)?.detail) || fallback;

// Same shape as a measurement date's folder list, plus each folder's policy flags.
export const useFirmLevelFoldersQuery = ({ firmId, companyId = null, enabled = true }: FirmLevelScopeParams) =>
  useQuery({
    enabled: Boolean(firmId) && enabled,
    queryFn: () => DocumentsService.apiDocumentsFirmsFirmFoldersList(String(firmId), companyId ?? undefined),
    queryKey: [FIRM_LEVEL_FOLDERS_QUERY_KEY, firmId, companyId ?? ''],
  });

// Counts for the collapsed Firm Documents row.
export const useFirmLevelSummaryQuery = ({ firmId, companyId = null, enabled = true }: FirmLevelScopeParams) =>
  useQuery({
    enabled: Boolean(firmId) && enabled,
    queryFn: () => DocumentsService.apiDocumentsFirmsFirmFoldersSummaryList(String(firmId), companyId ?? undefined),
    queryKey: [FIRM_LEVEL_SUMMARY_QUERY_KEY, firmId, companyId ?? ''],
  });

// Download all: a server-side zip job that keeps the folder tree, polled on the
// existing zip status endpoint.
export const useFirmFolderZipDownload = (firmId?: number | null) => {
  const { enqueueSnackbar } = useSnackbar();
  const firmKey = String(firmId);

  const onZipReady = useCallback((job: ZipJobStatus) => {
    globalThis.location.href = job.signed_url as string;
  }, []);
  const pollZipJob = useCallback(
    async (jobId: string) =>
      (await ExcelExportsService.apiExcelExportsFullCompanyReportsDownloadJobRead(firmKey, jobId)) as ZipJobStatus,
    [firmKey]
  );
  const {
    setDoReportPollCheck,
    setReportRequestData,
    setReportUUID,
    isLoading: isZipping,
  } = useCheckReportFile<{}, ZipJobStatus, void>({
    successMessage: 'Your download is ready.',
    defaultErrorMessage: 'An error occurred while preparing the download.',
    maxAttemptsMessage: 'The download is taking longer than expected. Please try again or contact support.',
    infoMessage: 'The download is taking longer than expected.',
    keepPollingStatusCodes: ZIP_KEEP_POLLING_STATUS_CODES,
    onSuccessFn: onZipReady,
    pollingFn: pollZipJob,
  });

  const { mutate: downloadAll, isPending } = useMutation({
    mutationFn: (folderId?: number | null) =>
      DocumentsService.apiDocumentsFirmsFirmFoldersDownloadAllCreate(firmKey, { folder_id: folderId ?? null }),
    onSuccess: ({ job_id: jobId }) => {
      setReportUUID(jobId);
      setDoReportPollCheck(true);
      // Truthy request data is what starts the poll.
      setReportRequestData({});
    },
    onError: (error: unknown) =>
      enqueueSnackbar(errorMessage(error, 'Failed to start the download.'), { variant: 'error' }),
  });

  return { downloadAll, isDownloadingAll: isPending || isZipping };
};
